import AlertIcon from '@assets/icons/alert.svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '@/utils/i18n';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  adaptChatMessage,
  MESSAGE_FRAGMENT,
  useGetChatMessages,
} from './hooks/use-get-chat-messages';
import { Analytics } from '@/utils/analytics';
import { CASEY_ID, LOADING_MESSAGE_ID } from '@/components/chats/constants';
import { Chat } from '@/components/chats/chat';
import { CHAT_ROUTES } from '@/navigators/routes';
import {
  ChatbotMessageRole,
  ChatDetails_AddMessageMutation,
  ChatDetails_AddMessageMutationVariables,
  ChatDetails_GetChatbotMessagesQuery,
  ChatDetails_GetChatbotMessagesQueryVariables,
  ChatDetails_SendChatbotMessageMutation,
  ChatDetails_SendChatbotMessageMutationVariables,
} from '@/types';
import { ChatStackParams } from '@/navigators/types';
import { ChatTab, ChatTabs } from '@/components/chats/chat-tabs';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, HEADER_PADDING_HORIZONTAL_SMALL, TYPOGRAPHY } from '@/styles';
import { gql, NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Message } from '@/components/chats/types';
import { OfflineComponent } from '@/components/offline-component';
import { OfflineDownloadProgressBar } from '@/components/offline-download-progress-bar';
import { PageContainer } from '@/components/page-container';
import { Platform, Text, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { UPLOAD_PREFIXES, uploadPublicImage } from '@/services/s3';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallbackOnce } from '@/hooks/use-callback-once';
import { useChatsContext } from '@/contexts/chats-context';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useIsFocused } from '@react-navigation/native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useSubscriptionContext } from '@/contexts/subscription-context';
import { useSupplierEmployees } from '@/hooks/use-supplier-employees';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';

const CHAT_LIST_PADDING_TOP = 20;
const CHATBOT_SESSION_KEY = 'CHATBOT_SESSION';

export const ADD_MESSAGE_MUTATION = gql`
  mutation ChatDetails_AddMessage(
    $chatUUID: uuid!
    $mediaContentURL: String
    $textContent: String
  ) {
    addMessageToChatFromRestaurantEmployee(
      chat_id: $chatUUID
      media_content_url: $mediaContentURL
      text_content: $textContent
    ) {
      message {
        ...ChatDetails_ChatMessage
      }
    }
  }

  ${MESSAGE_FRAGMENT}
`;

export const SEND_CHATBOT_MESSAGE_MUTATION = gql`
  mutation ChatDetails_SendChatbotMessage(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $sessionID: String
    $text: String!
  ) {
    sendChatbotMessage(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      session_id: $sessionID
      text: $text
    ) {
      messages {
        id
        text
        timestamp
      }
      session_id
    }
  }
`;

export const GET_CHATBOT_MESSAGE_QUERY = gql`
  query ChatDetails_GetChatbotMessages(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $sessionID: String!
  ) {
    getChatbotMessages(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      session_id: $sessionID
    ) {
      messages {
        id
        role
        text
        timestamp
      }
      session_id
    }
  }
`;

type Props = StackScreenProps<ChatStackParams, CHAT_ROUTES.Base>;
interface StoredChatbotSessionType {
  lastActivityDate: Date;
  sessionID: string;
}

export const ChatDetailsScreen: React.FC<Props> = ({ navigation, route }) => {
  const isFocused = useIsFocused();
  const { isConnected } = useSubscriptionContext();
  const isOfflineModeActive = useOfflineModeActive();
  const { markChatViewed } = useChatsContext();
  const {
    selectedRestaurantRelationship,
    supplierEmployeeRole,
    selectedRestaurantUUID,
    user: { uuid: userUUID, name },
  } = useAuthenticatedUserContext();
  const { isSmallLayout } = useLayoutContext();
  const baseHeaderProps = useHeaderRestaurantSelectorProps();
  const [showCannedQuestionActionSheet, setShowCannedQuestionActionSheet] =
    useState(false);
  const [styles, theme] = useStyles();

  const { chatUUID } = route.params;
  const {
    chatMessages,
    error: getChatMessagesError,
    fetchingMore,
    fetchMore,
    hasEarlierMessages,
    hasResponse: getChatMessagesHasResponse,
    lastCursor,
    networkStatus,
    setChatMessages,
  } = useGetChatMessages({ chatUUID, isFocused });
  const { supplierEmployeeUUIDs, hasResponse: supplierEmployeesHasResponse } =
    useSupplierEmployees(chatUUID);

  const { showErrorToast } = useToasts();

  const [addMessage] = useMutation<
    ChatDetails_AddMessageMutation,
    ChatDetails_AddMessageMutationVariables
  >(ADD_MESSAGE_MUTATION);

  const [sendChatbotMessage] = useMutation<
    ChatDetails_SendChatbotMessageMutation,
    ChatDetails_SendChatbotMessageMutationVariables
  >(SEND_CHATBOT_MESSAGE_MUTATION);

  const [sessionID, setSessionID] = useState<string>();
  const [getChatbotMessages, { loading }] = useLazyQuery<
    ChatDetails_GetChatbotMessagesQuery,
    ChatDetails_GetChatbotMessagesQueryVariables
  >(GET_CHATBOT_MESSAGE_QUERY);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const lastMessage = (chatMessages || [])[0];
    const lastMessageHasError = lastMessage ? lastMessage.error : false;
    const lastMessageRawCreatedAt = lastMessage?.rawCreatedAt;
    if (
      getChatMessagesHasResponse &&
      isFocused &&
      !lastMessageHasError &&
      lastMessageRawCreatedAt
    ) {
      markChatViewed(chatUUID, lastMessageRawCreatedAt);
    }
  }, [
    chatUUID,
    getChatMessagesHasResponse,
    isFocused,
    // Re-run effect if the last chat message has changed
    lastCursor,
    chatMessages,
    markChatViewed,
  ]);

  useEffect(() => {
    (async () => {
      if (selectedRestaurantRelationship.showChatbot && supplierEmployeeRole) {
        const storedSession = await AsyncStorage.getItem(CHATBOT_SESSION_KEY);
        const sessionProperties: StoredChatbotSessionType = storedSession
          ? JSON.parse(storedSession)
          : {};
        if (
          sessionProperties.lastActivityDate &&
          moment(sessionProperties.lastActivityDate).isAfter(
            moment().subtract(3, 'days'),
          ) &&
          sessionProperties.sessionID &&
          chatbotMessages.length === 0 &&
          !loading
        ) {
          setSessionID(sessionProperties.sessionID);
          try {
            const messages = (
              await getChatbotMessages({
                variables: {
                  restaurantUUID: selectedRestaurantUUID,
                  sessionID: sessionProperties.sessionID,
                  supplierUUID: selectedRestaurantRelationship.supplierUUID,
                },
              })
            ).data?.getChatbotMessages.messages;

            const formattedMessages: Message[] =
              messages?.map((message) => {
                return {
                  _id: message.id,
                  createdAt: moment.utc(message.timestamp).local().toDate(),
                  error: null,
                  text: message.text,
                  user:
                    message.role === ChatbotMessageRole.Chatbot
                      ? {
                          _id: CASEY_ID,
                        }
                      : chatUser,
                };
              }) || [];
            setChatbotMessages(formattedMessages);
          } catch (e) {
            showErrorToast();
          }
        }
      }
    })();
  }); // [ASK] Should't it depend on [selectedRestaurantRelationship, supplierEmployeeRole]?

  const onError = useCallbackOnce(
    useCallback(() => {
      showErrorToast();
      navigation.goBack();
    }, [navigation, showErrorToast]),
  );

  useEffect(() => {
    // Ignore network errors when displaying the toast (GQL error will still persist)
    // since most network errors are unresolvable and can be easily triggerd by Offline Mode
    if (getChatMessagesError && networkStatus !== NetworkStatus.error) {
      onError();
    }
  }, [getChatMessagesError, networkStatus, onError]);

  const chatUser = useMemo(
    () => ({
      _id: userUUID,
      name,
    }),
    [userUUID, name],
  );

  const [selectedTab, setSelectedTab] = useState<ChatTab>(ChatTab.Customer);
  const [displayPrompts, setDisplayPrompts] = useState(false);
  const [chatbotMessages, setChatbotMessages] = useState<Message[]>([]);
  const [isSendingChatbotMessage, setIsSendingChatbotMessage] = useState(false);
  const abortControllerRef = useRef<AbortController>(null);

  const onPressPrompt = (message: string) => {
    setDisplayPrompts(false);
    onSendChatbotMessage({
      _id: uuidv4(),
      createdAt: new Date(),
      error: null,
      text: message,
      user: chatUser,
    });
  };

  const onSendChatMessage = async (draftMessage: Message) => {
    try {
      const addMessageVariables: ChatDetails_AddMessageMutationVariables = {
        chatUUID,
      };

      if (draftMessage.image) {
        addMessageVariables.mediaContentURL = await uploadPublicImage(
          chatUUID,
          draftMessage.image,
          UPLOAD_PREFIXES.Chats,
        );
      } else {
        addMessageVariables.textContent = draftMessage.text;
      }

      const { data } = await addMessage({
        variables: addMessageVariables,
      });

      return adaptChatMessage(
        data?.addMessageToChatFromRestaurantEmployee.message!,
      );
    } catch (e) {
      showErrorToast();
    }

    return null;
  };

  const cancelOngoingChatbotRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsSendingChatbotMessage(false);
      setChatbotMessages((prev) =>
        prev.filter((m) => m._id !== LOADING_MESSAGE_ID),
      );
    }
  };

  const onSendChatbotMessage = async (draftMessage: Message) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // remove current loading message
      setChatbotMessages((prev) => [
        ...prev.filter((m) => m._id !== LOADING_MESSAGE_ID),
      ]);
    }

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    setDisplayPrompts(false);
    setIsSendingChatbotMessage(true);
    setChatbotMessages((prev) => [draftMessage, ...prev]);

    const loadingResponse: Message = {
      _id: LOADING_MESSAGE_ID,
      createdAt: new Date(),
      error: null,
      text: '',
      user: {
        _id: CASEY_ID,
      },
    };

    setChatbotMessages((prev) => [loadingResponse, ...prev]);
    try {
      const messageResponse = (
        await sendChatbotMessage({
          context: {
            fetchOptions: {
              signal: abortControllerRef.current.signal,
            },
          },
          variables: {
            restaurantUUID: selectedRestaurantUUID,
            sessionID,
            supplierUUID: selectedRestaurantRelationship.supplierUUID,
            text: draftMessage.text,
          },
        })
      ).data?.sendChatbotMessage;

      setSessionID(messageResponse?.session_id);
      const messageContent = messageResponse?.messages[0];
      if (messageContent) {
        const chatBotResponse: Message = {
          _id: messageContent.id,
          createdAt: moment.utc(messageContent.timestamp).local().toDate(),
          error: null,
          text: messageContent.text,
          user: {
            _id: CASEY_ID,
          },
        };

        setChatbotMessages((prev) => [
          chatBotResponse,
          ...prev.filter((m) => m._id !== LOADING_MESSAGE_ID),
        ]);
        await AsyncStorage.setItem(
          CHATBOT_SESSION_KEY,
          JSON.stringify({
            lastActivityDate: new Date(),
            sessionID: messageResponse?.session_id,
          }),
        );
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        // do nothing
      } else {
        showErrorToast();
      }
    }

    setIsSendingChatbotMessage(false);
    abortControllerRef.current = null;
    return null;
  };

  const chatContent = (
    <>
      <Chat
        isCaseyAi={selectedTab === ChatTab.Chatbot}
        isDisplayingPrompts={displayPrompts}
        isLoadingEarlier={fetchingMore}
        isSendingChatbotMessage={isSendingChatbotMessage}
        listViewProps={{
          contentContainerStyle: styles.chatListContentContainer,
        }}
        loadEarlier={hasEarlierMessages}
        loading={!getChatMessagesHasResponse || !supplierEmployeesHasResponse}
        messages={
          selectedTab === ChatTab.Chatbot ? chatbotMessages : chatMessages || []
        }
        onLoadEarlier={fetchMore}
        onPressPrompt={onPressPrompt}
        onSend={
          selectedTab === ChatTab.Chatbot
            ? onSendChatbotMessage
            : onSendChatMessage
        }
        setMessages={
          selectedTab === ChatTab.Chatbot
            ? () => chatbotMessages
            : setChatMessages
        }
        setShowCannedQuestionActionSheet={setShowCannedQuestionActionSheet}
        showCannedQuestionActionSheet={showCannedQuestionActionSheet}
        supplierEmployeeUUIDs={supplierEmployeeUUIDs}
        user={chatUser}
      />
      {selectedRestaurantRelationship.showChatbot && supplierEmployeeRole ? (
        <ChatTabs
          isNewSession={
            selectedTab === ChatTab.Chatbot && chatbotMessages.length !== 0
          }
          onPressChatbot={async () => {
            setSelectedTab(ChatTab.Chatbot);
            if (chatbotMessages.length === 0) {
              setDisplayPrompts(true);
            }

            if (sessionID) {
              await AsyncStorage.setItem(
                CHATBOT_SESSION_KEY,
                JSON.stringify({
                  lastActivityDate: new Date(),
                  sessionID,
                }),
              );
            }
            Analytics.track('chat.chatbot_tab.press', {
              restaurantUUID: selectedRestaurantUUID,
              selectedTab: ChatTab.Chatbot,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
              userUUID,
            });
          }}
          onPressCustomer={() => {
            setSelectedTab(ChatTab.Customer);
            setDisplayPrompts(false);
            Analytics.track('chat.chatbot_tab.press', {
              restaurantUUID: selectedRestaurantUUID,
              selectedTab: ChatTab.Customer,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
              userUUID,
            });
          }}
          onPressNewSession={async () => {
            await AsyncStorage.removeItem(CHATBOT_SESSION_KEY);
            setSessionID(undefined);
            setChatbotMessages([]);
            setDisplayPrompts(true);
            cancelOngoingChatbotRequest();
          }}
          selectedTab={selectedTab}
        />
      ) : null}
      {!isConnected ? (
        <View style={styles.statusMessage}>
          <AlertIcon fill={theme.colors.white} height={16} width={16} />
          <Text
            numberOfLines={1}
            style={[
              TYPOGRAPHY.small,
              FONTS.primarySemiBold,
              styles.statusMessageText,
            ]}
          >
            {i18n.t('no_internet_connection')}
          </Text>
        </View>
      ) : null}
    </>
  );

  if (!isSmallLayout) {
    return isOfflineModeActive && chatMessages?.length === 0 ? (
      <OfflineComponent />
    ) : (
      chatContent
    );
  }

  return (
    <PageContainer
      navigationHeaderProps={{
        ...baseHeaderProps,
        // Show download progress bar on small layouts
        // since the navigation header content is hidden
        after: isSmallLayout ? <OfflineDownloadProgressBar /> : null,
        contentStyle: styles.navigationHeaderContent,
      }}
      statusBarColor={theme.colors.background}
    >
      {isOfflineModeActive && chatMessages?.length === 0 ? (
        <OfflineComponent />
      ) : (
        chatContent
      )}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  chatListContentContainer: {
    paddingBottom: CHAT_LIST_PADDING_TOP,
  },
  navigationHeaderContent: {
    // Replaced display: 'none' with opacity: 0 to prevent Yoga layout crashes in RN 0.79+
    // See: https://github.com/facebook/react-native/issues/52349
    // Using opacity instead of display: 'none' maintains hiding behavior without layout issues
    // Scoped to Android only as this is an Android-specific crash
    ...Platform.select({
      android: {
        height: 0,
        opacity: 0,
        pointerEvents: 'none' as const,
        width: 0,
      },
      default: {
        display: 'none' as const,
      },
    }),
  },
  statusMessage: {
    alignItems: 'center',
    backgroundColor: theme.colors.overlay,
    flexDirection: 'row',
    justifyContent: 'center',
    left: 0,
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
    paddingVertical: 12,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  statusMessageText: {
    color: theme.colors.white,
    marginLeft: 8,
  },
}));
